import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  ChartLineIcon,
  FileIcon,
  GlobeIcon,
  LinkIcon,
  MegaphoneIcon,
  SettingsIcon,
  TargetIcon,
  UploadIcon,
  UsersIcon,
  ZapIcon,
} from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { getSidebarIcon } from "simplydo/react/components/SidebarIcons";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type { AdminSidebarItem } from "./types";

export const defaultAdminSidebarItems: AdminSidebarItem[] = [
  {
    id: "overview",
    translate: "generic.overview",
    icon: GlobeIcon,
    location: ".",
    activePath: "^/admin$",
  },
  {
    id: "activity",
    translate: "generic.activity",
    icon: ChartLineIcon,
    location: "activity",
    activePath: "activity",
    permissions: ["org.manageChallenges", "org.changeSettings"],
  },
  {
    id: "users",
    translate: "admin.admin.links.users",
    icon: UsersIcon,
    location: "users",
    activePath: "users|admins",
    permissions: ["org.listUsers"],
  },
  {
    id: "challenges",
    translate: "generic.challenges",
    icon: TargetIcon,
    location: "challenges",
    activePath: "challenges",
    permissions: ["org.manageChallenges"],
  },
  {
    id: "communication",
    translate: "generic.communication",
    icon: MegaphoneIcon,
    location: "communication",
    activePath: "communication(?!.*polls)",
    permissions: [
      "org.manageAnnouncements",
      "org.viewAnnouncementLogs",
      "org.changeSettings",
    ],
  },
  {
    id: "uploads",
    translate: "admin.admin.links.uploads",
    icon: UploadIcon,
    location: "uploads",
    activePath: "uploads",
    permissions: ["org.uploadFiles"],
  },
  {
    id: "polls",
    translate: "admin.admin.links.polls",
    icon: ZapIcon,
    location: "polls",
    activePath: "polls",
    permissions: ["org.managePolls"],
  },
  {
    id: "links",
    translate: "generic.links",
    icon: LinkIcon,
    location: "links",
    activePath: "links",
  },
  {
    id: "settings",
    translate: "generic.settings",
    icon: SettingsIcon,
    location: "settings",
    activePath: "settings",
    permissions: ["org.changeSettings"],
    required: true,
  },
];

/**
 * Resolves the admin area sidebar from `organisation.adminSidebar`, which stores
 * an ordered list of `{ id, type? }` references. Built-in entries come from the
 * catalog above, custom pages and links are hydrated at runtime.
 */
export const useAdminSidebar = () => {
  const user = useAppSelector((state) => state.user);
  const orgId = user?.ownerOrganisation?._id;
  const customSidebar = user?.ownerOrganisation?.adminSidebar;
  const customPagesEnabled = util.organisationFeaturesEnabled(user, [
    "customPages",
  ]);

  const pagesQuery = useQuery(
    initUseQuery(api.pages.getAll, ["organisation", orgId ?? ""], {
      isEnabled: !!orgId && customPagesEnabled,
    }),
  );

  const allItems = useMemo<AdminSidebarItem[]>(
    () =>
      defaultAdminSidebarItems
        .concat(
          (pagesQuery.data?.pages ?? []).map((page) => ({
            id: page._id,
            title: page.title,
            type: "page",
            icon: FileIcon,
            location: `custom-pages/${page._id}`,
            activePath: `custom-pages/${page._id}`,
            // This listing returns every page to page and permission managers,
            // so mirror the API's can_view_page rule to decide what to render
            canView:
              !!page.isPublic ||
              util.hasPermission(user, "org.managePages", orgId) ||
              util.hasPermission(user, "page.view", page._id, orgId),
            visibleUserCount: page.visibleUserCount,
            visibleRoleNames: page.visibleRoleNames,
          })),
        )
        .concat(
          (user?.ownerOrganisation?.links ?? []).map((link) => ({
            ...link,
            type: "link",
            icon: LinkIcon,
          })),
        ),
    [pagesQuery.data, user, orgId],
  );

  // The configured order, without permission filtering, so that an admin who
  // cannot see every entry does not drop the others when saving. References
  // that cannot be resolved at all are kept for the same reason.
  const configuredEntries = useMemo<AdminSidebarItem[]>(() => {
    if (!customSidebar?.length) {
      return defaultAdminSidebarItems;
    }
    return customSidebar.map((entry) => {
      const currentItem = allItems.find((item) => item.id === entry.id);
      if (!currentItem) {
        return {
          id: entry.id,
          type: entry.type,
          iconName: entry.icon,
          icon: getSidebarIcon(entry.icon),
        };
      }
      return {
        ...currentItem,
        iconName: entry.icon,
        icon: getSidebarIcon(entry.icon) ?? currentItem.icon,
      };
    });
  }, [customSidebar, allItems]);

  const entries = useMemo(
    () =>
      configuredEntries.filter((entry) => {
        if (!entry.location || entry.canView === false) {
          return false;
        }
        if (!entry.permissions) {
          return true;
        }
        return !!entry.permissions.find((permission) =>
          util.hasPermission(user, permission, orgId),
        );
      }),
    [configuredEntries, user, orgId],
  );

  return { entries, configuredEntries, allItems };
};
